import React, { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Home } from "lucide-react";
import { buildAppPath } from "@/lib/site-url";

interface Props { children: ReactNode }
interface State { hasError: boolean }

export class ErrorBoundary extends Component<Props, State> {
    public state: State = { hasError: false };

    public static getDerivedStateFromError(_: Error): State {
        return { hasError: true };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error("Uncaught error:", error, errorInfo);

        // Auto-heal Vite chunk mismatch during new deployments
        const message = error.message?.toLowerCase() || "";
        if (
            message.includes("failed to fetch dynamically imported module") ||
            message.includes("importing a module script failed") ||
            message.includes("error loading dynamically imported module") ||
            message.includes("loading chunk") ||
            message.includes("dynamically imported module")
        ) {
            console.warn("Stale build chunk detected by ErrorBoundary. Auto-reloading to fetch newest deployment...");
            // Use a slight delay to avoid infinite reload loops
            setTimeout(() => window.location.reload(), 300);
        }
    }

    public render() {
        if (this.state.hasError) {
            return (
                <div className="flex flex-col items-center justify-center min-h-[50vh] p-4 text-center space-y-4">
                    <div className="h-12 w-12 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
                        <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
                    </div>
                    <h2 className="text-xl font-bold">Something went wrong</h2>
                    <p className="text-muted-foreground max-w-xs">
                        We encountered an unexpected error. Try reloading the page.
                    </p>
                    <div className="flex gap-2">
                        <Button onClick={() => window.location.reload()}>Reload Page</Button>
                        <Button variant="outline" onClick={() => window.location.assign(buildAppPath("/"))}>
                            <Home className="me-2 h-4 w-4" /> Go Home
                        </Button>
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}
